import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    CheckCircle,
    Loader2,
    AlertCircle,
    ArrowRight,
    Shield,
    Users
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

export default function TeamInvitePage() {
    const { token } = useParams();
    const { t } = useTranslation();

    // Account creation state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [creatingAccount, setCreatingAccount] = useState(false);
    const [errors, setErrors] = useState({});

    // Validate token and get basic info
    const { data: inviteInfo, isLoading, error } = useQuery({
        queryKey: ['team-invite', token],
        queryFn: async () => {
            const response = await api.get(`/team/invite/${token}`, { skipAuth: true });
            return response;
        },
        retry: 1
    });

    const handleCreateAccount = async (e) => {
        e.preventDefault();
        setErrors({});
        const newErrors = {};

        // Validation
        if (!password) {
            newErrors.password = t('auth.login.password') + " is required";
        }

        if (password && password.length < 8) {
            newErrors.password = t('auth.signup.passwordTooShort') || "Password must be at least 8 characters";
        } else if (password) {
            // Complexity check
            const hasUpperCase = /[A-Z]/.test(password);
            const hasLowerCase = /[a-z]/.test(password);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

            if (!hasUpperCase || !hasLowerCase || !hasSpecialChar) {
                newErrors.password = "Password must contain at least 1 uppercase, 1 lowercase, and 1 special character";
            }
        }

        if (password !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setCreatingAccount(true);
        try {
            const data = await api.post(`/team/invite/${token}/convert-to-user`, {
                password
            }, { skipAuth: true });

            // Success! Login and redirect
            localStorage.setItem('token', data.token);
            toast.success("Account created successfully!");

            // Force reload to update auth state and redirect to dashboard
            window.location.href = '/analytics';

        } catch (err) {
            toast.error(err.message || "Failed to create account");
        } finally {
            setCreatingAccount(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/30">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !inviteInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/30">
                <Card className="max-w-md">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">{t('invite.error.title')}</h2>
                        <p className="text-muted-foreground">
                            {t('invite.error.message') || "This invite link is invalid or has expired."}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/30 flex flex-col">
            {/* Header */}
            <header className="bg-background border-b sticky top-0 z-10 w-full">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center">
                        <img
                            src="/assets/pocketfiler-header-logo.png"
                            alt="PocketFiler"
                            className="h-20"
                            style={{ objectFit: 'contain' }}
                        />
                    </div>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-md animate-fade-in shadow-lg">
                    <CardHeader className="text-center pb-2">
                        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                            <Users className="h-8 w-8 text-accent" />
                        </div>
                        <CardTitle className="text-2xl">Join Your Team</CardTitle>
                        <CardDescription className="text-base">
                            You've been invited to join <strong>{inviteInfo.organization_name}</strong> on PocketFiler.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">

                        <div className="p-4 rounded-xl border-2 border-primary/20 bg-primary/5 mb-6">
                            <h3 className="text-lg font-semibold mb-1 text-center">
                                Set Up Your Account
                            </h3>
                            <p className="text-sm text-center text-muted-foreground mb-6">
                                Hello {inviteInfo.name}, please set a password for your account <strong>{inviteInfo.email}</strong>.
                            </p>

                            <form onSubmit={handleCreateAccount} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium" htmlFor="password">
                                        Password <span className="text-destructive ml-1">*</span>
                                    </label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (errors.password) setErrors(prev => ({ ...prev, password: null }));
                                        }}
                                        placeholder="Min. 8 characters"
                                        required
                                        maxLength={100}
                                        className={errors.password ? "border-destructive" : ""}
                                    />
                                    {errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium" htmlFor="confirmPassword">
                                        Confirm Password <span className="text-destructive ml-1">*</span>
                                    </label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: null }));
                                        }}
                                        placeholder="Re-enter password"
                                        required
                                        maxLength={100}
                                        className={errors.confirmPassword ? "border-destructive" : ""}
                                    />
                                    {errors.confirmPassword && <p className="text-xs text-destructive mt-1">{errors.confirmPassword}</p>}
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full mt-6"
                                    size="lg"
                                    disabled={creatingAccount}
                                >
                                    {creatingAccount ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Joining Organization...
                                        </>
                                    ) : (
                                        <>
                                            Create Account & Join
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted text-left">
                            <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground">
                                By creating an account, you agree to securely share information across PocketFiler within your organization.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
